/**
 * useServiceQueuePaginated - Hook paginado server-side para fila de atendimento
 * 
 * Utiliza a função RPC get_service_queue_paginated seguindo arquitetura do projeto.
 * Implementa paginação offset-based igual ao useCrmDealsPaginated.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { QueueFilters, QueueSituation } from '@/hooks/useServiceQueue';

// Logger condicional para debug
const logger = createLogger('ServiceQueuePaginated');

/**
 * Campos permitidos para ordenação (whitelist segura)
 * Mapeados para colunas do banco de dados
 */
export type QueueSortField = 
  | 'last_message_at' 
  | 'created_at' 
  | 'contact_name' 
  | 'inbox_name' 
  | 'department_name' 
  | 'unread_count'
  | 'waiting_seconds';

/**
 * Direção de ordenação
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Interface para item da fila retornado pela RPC paginada
 * Estrutura flat otimizada para tabelas
 */
export interface PaginatedQueueItem {
  id: string;
  contact_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_avatar_url: string | null;
  inbox_id: string;
  inbox_name: string | null;
  inbox_identifier: string | null;
  department_id: string | null;
  department_name: string | null;
  assigned_agent_id: string | null;
  assigned_agent_name: string | null;
  assigned_agent_avatar: string | null;  // v1.2.0
  status: string;
  situation: QueueSituation;
  created_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  waiting_seconds: number;
  has_pending_transfer: boolean;
  transfer_id: string | null;
  transfer_to_agent_id: string | null;
  transfer_to_agent_name: string | null;
  transfer_to_department_id: string | null;
  transfer_to_department_name: string | null;
  transfer_from_agent_id: string | null;
  transfer_from_agent_name: string | null;
  transfer_created_at: string | null;
  total_count: number;
}

/**
 * Parâmetros de paginação e ordenação
 */
interface PaginationParams {
  page: number;
  pageSize: number;
  sortField?: QueueSortField;
  sortDirection?: SortDirection;
}

/**
 * Resultado da query paginada
 */
interface PaginatedQueueResult {
  items: PaginatedQueueItem[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook para buscar itens da fila com paginação server-side
 * Utiliza RPC get_service_queue_paginated para performance em grande escala
 * 
 * @param filters - Filtros para a busca (situation, department, inbox, agent, search)
 * @param pagination - Parâmetros de paginação (page, pageSize, sortField, sortDirection)
 * @param options - Opções adicionais (enabled para controle condicional)
 */
export function useServiceQueuePaginated(
  filters: QueueFilters,
  pagination: PaginationParams,
  options?: { enabled?: boolean }
): PaginatedQueueResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    // Query key inclui filtros e paginação para cache correto
    queryKey: ['service-queue-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando itens da fila paginados:', { filters, pagination });

      // Chama RPC com todos os parâmetros
      const { data, error } = await supabase.rpc(
        'get_service_queue_paginated' as any,
        {
          p_page: pagination.page,
          p_page_size: pagination.pageSize,
          p_sort_field: pagination.sortField || 'last_message_at',
          p_sort_direction: pagination.sortDirection || 'desc',
          // Filtros - converte 'all' para null
          p_situation: filters.situation === 'all' ? null : filters.situation,
          p_department_id: filters.departmentId || null,
          p_inbox_id: filters.inboxId || null,
          p_agent_id: filters.agentId || null,
          p_search: filters.search || null,
          p_tenant_hint: currentTenant?.id || null,
        }
      );

      if (error) {
        logger.error('Erro ao buscar itens da fila paginados:', error);
        throw error;
      }

      // Total count vem em cada row (pattern COUNT(*) OVER())
      const rawItems = (data || []) as PaginatedQueueItem[];
      const totalCount = rawItems[0]?.total_count ?? 0;

      logger.debug(`Recebidos ${rawItems.length} itens, total: ${totalCount}`);

      return {
        items: rawItems,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
      };
    },
    enabled: (options?.enabled ?? true) && !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    items: query.data?.items || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['service-queue-paginated', currentTenant?.id] 
      });
    },
  };
}
